'use client';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { PHONE_MAX_WIDTH } from './breakpoints';

/** The viewport against the mobile breakpoint (≤ 767px) — mirrors the Sidebar's drawer threshold so the
 *  two never disagree on what "mobile" means. `undefined` until the first effect measures it: neither SSR
 *  nor the first paint knows the viewport, so a layout that must not mount the WRONG variant even for a
 *  single commit (a phone building a desktop side column) waits for the boolean instead of guessing. */
export function useMobileViewport(): boolean | undefined {
  return useMediaQuery(`(max-width: ${PHONE_MAX_WIDTH}px)`);
}

/** Internal viewport query shared by the phone boundary and the shell's advisor column. */
export function useMediaQuery(query: string): boolean | undefined {
  const [matches, setMatches] = useState<boolean | undefined>(undefined);
  useEffect(() => {
    const media = window.matchMedia(query);
    setMatches(media.matches);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

/** The same viewport check collapsed to a boolean, with "not measured yet" reading as desktop. SSR-safe
 *  (server and first paint agree on false). For a layout whose desktop branch is the safe first paint —
 *  it only ever gains a drawer on a narrow screen — this avoids a blank frame. */
export function useMobile(): boolean {
  return useMobileViewport() === true;
}

/** Whether the primary input is touch-like: a coarse pointer or no hover. Focusing a text field there
 *  summons the on-screen keyboard, so overlays must not autofocus one (an overlay search field would pop
 *  the keyboard on every open). Read synchronously through the external store, so a surface mounted on open
 *  already knows before the overlay's own focus effect runs; `useMediaQuery` would still read its
 *  not-measured-yet default in that commit. SSR-safe: the server snapshot is `false`, so server HTML keeps
 *  the desktop behaviour and a phone corrects after hydration, before anyone can open an overlay. */
export function useCoarsePointer(): boolean {
  return useSyncExternalStore(subscribeTouchLike, readTouchLike, () => false);
}

const COARSE_POINTER_QUERY = '(pointer: coarse)';
const NO_HOVER_QUERY = '(hover: none)';

function subscribeTouchLike(onChange: () => void): () => void {
  const coarse = window.matchMedia(COARSE_POINTER_QUERY);
  const hover = window.matchMedia(NO_HOVER_QUERY);
  coarse.addEventListener('change', onChange);
  hover.addEventListener('change', onChange);
  return () => {
    coarse.removeEventListener('change', onChange);
    hover.removeEventListener('change', onChange);
  };
}

function readTouchLike(): boolean {
  return window.matchMedia(COARSE_POINTER_QUERY).matches || window.matchMedia(NO_HOVER_QUERY).matches;
}
